import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Pencil } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { getColorById } from '../data/mockData';
import { useTranslation } from '../context/TranslationContext';
import { getColorByRAL } from '../data/ralColors';

// Yes/No Toggle Component
const YesNoToggle = ({ label, value, onChange }) => {
  const isYes = value === true;
  const isNo = value === false;
  
  return (
    <div className="flex items-center justify-between p-2 border border-gray-200 rounded-md bg-white hover:bg-gray-50 transition-colors">
      <Label className="text-xs text-gray-700 cursor-pointer flex-1">{label}</Label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onChange(true)}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
            isYes
              ? 'bg-green-600 text-white shadow-sm'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
        >
          YES
        </button>
        <button
          type="button"
          onClick={() => onChange(false)}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
            isNo
              ? 'bg-red-600 text-white shadow-sm'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
        >
          NO
        </button>
      </div>
    </div>
  );
};

const Quote = ({ 
  open, 
  onOpenChange, 
  config, 
  pricing, 
  profileSystems, 
  glassTextures, 
  glazingTypes, 
  colors, 
  raamtypes, 
  hardware, 
  accessories, 
  woodJointOptions, 
  partsOptions, 
  overheadOptions,
  drawingBoardRef,
  actionMarginTopRem, // optional: allow pages (e.g. Terrasdeur) to override button bar top margin
  currentQuantityInput // current quantity input value from Summary component
}) => {
  const { t } = useTranslation();
  
  // Helper function to translate glass texture and glazing type names
  const translateGlassTextureName = (id) => {
    const translations = {
      'helder-glas': t('options.clearGlass'),
      'chinchilla': t('options.chinchilla'),
      'satinato': t('options.satinato')
    };
    return translations[id] || id;
  };
  
  const translateGlazingTypeName = (id) => {
    const translations = {
      'double': t('options.doubleGlass24'),
      'triple': t('options.doubleGlass36'),
      'double-24': t('options.doubleGlass24'),
      'double-36': t('options.doubleGlass36')
    };
    return translations[id] || id;
  };
  const [clientDetails, setClientDetails] = useState({
    name: '',
    address: '',
    city: '',
    postalCode: '',
    phone: '',
    email: '',
    notes: '',
    signature: '', // Will store base64 image data
    subjectToFinance: false, // Subject to finance option
    // Additional Options
    clickOnColorAndSize: false,
    screenElectricallyOperated: false,
    rollerShutterElectricallyOperated: false,
    // Home Finishing
    buoySharing: false,
    typesetting: false,
    container: false,
    interiorFinish: false,
    // Lifting Support
    crane: false,
    craneTrips: '',
    liftingAssistance: false,
    liftingAssistancePersonnel: '',
    // Execution
    colorFrameFrame: '',
    wing: '',
    inside: '',
    typeOfGlass: '',
    // Comments
    comments: ''
  });
  const [isEditing, setIsEditing] = useState(true);
  const [isSaved, setIsSaved] = useState(false);

  const quoteRef = useRef(null);
  const signatureCanvasRef = useRef(null);
  const [quoteNumber, setQuoteNumber] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  // Initialize currentQuantity from config.quantity if available, otherwise default to 1
  const initialQuantity = (config?.quantity !== undefined && config?.quantity !== null && !isNaN(config?.quantity) && Number(config?.quantity) >= 1) 
    ? Number(config?.quantity) 
    : 1;
  const [currentQuantity, setCurrentQuantity] = useState(initialQuantity);

  // Initialize signature canvas
  useEffect(() => {
    if (signatureCanvasRef.current && isEditing) {
      const canvas = signatureCanvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // If there's a saved signature, draw it
      if (clientDetails.signature) {
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
        };
        img.src = clientDetails.signature;
      }
    }
  }, [isEditing, clientDetails.signature]);

  // Generate quote number on mount and reset edit state
  useEffect(() => {
    if (open) {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const random = Math.floor(Math.random() * 1000);
      setQuoteNumber(`OFF-${year}${month}${day}-${random}`);
      // Reset to editing mode when modal opens
      setIsEditing(true);
      setIsSaved(false);
      // Update quantity when modal opens - use currentQuantityInput if available, otherwise fall back to config.quantity
      const qty = (currentQuantityInput !== undefined && currentQuantityInput !== null && !isNaN(currentQuantityInput) && Number(currentQuantityInput) >= 1)
        ? Number(currentQuantityInput)
        : (config?.quantity !== undefined && config?.quantity !== null && !isNaN(config?.quantity) && Number(config?.quantity) >= 1)
          ? Number(config?.quantity)
          : 1;
      setCurrentQuantity(qty);
    }
  }, [open, config?.quantity]);
  
  // Update current quantity whenever config.quantity changes (even when modal is open)
  useEffect(() => {
    const qty = config?.quantity;
    if (qty !== undefined && qty !== null && !isNaN(qty) && Number(qty) >= 1) {
      setCurrentQuantity(Number(qty));
    } else {
      setCurrentQuantity(1);
    }
  }, [config?.quantity]);

  // Format date based on language
  const formatDate = (date) => {
    const d = new Date(date);
    const language = localStorage.getItem('language') || 'en';
    
    if (language === 'nl') {
      const months = [
        'januari', 'februari', 'maart', 'april', 'mei', 'juni',
        'juli', 'augustus', 'september', 'oktober', 'november', 'december'
      ];
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
    } else {
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    }
  };

  // Calculate prices (same logic as Summary component)
  const calculatePrices = () => {
    const areaSqm = (config.width * config.height) / 1000000;
    // Base price: 1350 per m² (standard pricing)
    const BASE_PRICE_PER_SQM = 1350;
    const basePrice = BASE_PRICE_PER_SQM * areaSqm;
    const partsMultiplier = (config.designType === undefined && config.topLight === undefined) ? (pricing.parts[config.parts] || 1) : 1;
    const finalBasePrice = basePrice * partsMultiplier;
    
    let totalOptions = 0;
    const optionItems = [];
    
    // Profile System
    const profileSystem = profileSystems.find(p => p.id === config.profileSystem);
    const profilePrice = pricing.profileSystem[config.profileSystem] || 0;
    optionItems.push({ name: `Profile: ${profileSystem?.name || config.profileSystem}`, price: profilePrice });
    totalOptions += profilePrice;
    
    // Glass Texture
    const glassTexture = glassTextures.find(g => g.id === config.glassTexture);
    const glassPrice = pricing.glassTexture[config.glassTexture] || 0;
    optionItems.push({ name: `Glass: ${glassTexture?.name || config.glassTexture}`, price: glassPrice });
    totalOptions += glassPrice;
    
    // Glazing Type
    const glazingType = glazingTypes.find(g => g.id === config.glazingType);
    const glazingPrice = pricing.glazingType[config.glazingType] || 0;
    optionItems.push({ name: `Glazing: ${translateGlazingTypeName(config.glazingType)}`, price: glazingPrice });
    totalOptions += glazingPrice;
    
    // External Frame Color
    const color = colors.find(c => c.id === (config.exteriorColor || config.color));
    const colorPrice = (config.exteriorColor || config.color) === 'anthracite' ? 0 : (pricing.color[config.exteriorColor || config.color] || 0);
    optionItems.push({ name: `External Color: ${color?.name || (config.exteriorColor || config.color)}`, price: colorPrice });
    totalOptions += colorPrice;
    
    // Interior Frame Color
    const hasInteriorFrames = config.partWindowTypes?.some(type => 
      ['fixed-glass', 'tilt-only', 'tilt-turn-left', 'tilt-turn-right'].includes(type)
    ) || config.overheadPartWindowTypes?.some(type => 
      ['fixed-glass', 'tilt-only', 'tilt-turn-left', 'tilt-turn-right'].includes(type)
    );
    
    if (hasInteriorFrames) {
      const interiorColor = colors.find(c => c.id === (config.interiorColor || config.interiorFrameColor));
      const interiorColorPrice = (config.interiorColor || config.interiorFrameColor) === 'white' ? 0 : (pricing.interiorFrameColor[config.interiorColor || config.interiorFrameColor] || 0);
      optionItems.push({ name: `Interior Color: ${interiorColor?.name || (config.interiorColor || config.interiorFrameColor)}`, price: interiorColorPrice });
      totalOptions += interiorColorPrice;
    }
    
    // Back Door (Achterdeur) specific options
    if (config.topLight !== undefined || config.numberOfParts !== undefined) {
      // Top Light
      const topLightPrice = pricing.achterdeur?.topLight?.[config.topLight] || 0;
      if (topLightPrice > 0) {
        const topLightNames = {
          'no-top-light': 'No Top Light',
          'with-top-light': 'With Top Light'
        };
        optionItems.push({ name: `Top Light: ${topLightNames[config.topLight] || config.topLight}`, price: topLightPrice });
        totalOptions += topLightPrice;
      }
      
      // Number of Parts
      const numberOfPartsPrice = pricing.achterdeur?.numberOfParts?.[config.numberOfParts] || 0;
      if (numberOfPartsPrice > 0) {
        const numberOfPartsNames = {
          'one-part': 'One Part',
          'two-parts': 'Two Parts'
        };
        optionItems.push({ name: `Parts: ${numberOfPartsNames[config.numberOfParts] || config.numberOfParts}`, price: numberOfPartsPrice });
        totalOptions += numberOfPartsPrice;
      }
      
      // Panel Type (only when two-parts is selected)
      if (config.numberOfParts === 'two-parts' && config.panelType) {
        const panelTypePrice = pricing.achterdeur?.panelType?.[config.panelType] || 0;
        if (panelTypePrice > 0) {
          const panelTypeNames = {
            'sandwich-no-mullions': 'Sandwich Panel (No Mullions)',
            'glass': 'Glass',
            'horizontal-mullions': 'Horizontal Mullions',
            'vertical-mullions': 'Vertical Mullions'
          };
          optionItems.push({ name: `Panel Type: ${panelTypeNames[config.panelType] || config.panelType}`, price: panelTypePrice });
          totalOptions += panelTypePrice;
        }
      }
      
      // Threshold Height
      const thresholdPrice = pricing.achterdeur?.thresholdHeight?.[config.thresholdHeight] || 0;
      if (thresholdPrice > 0) {
        optionItems.push({ name: `Threshold: ${config.thresholdHeight}`, price: thresholdPrice });
        totalOptions += thresholdPrice;
      }
      
      // Parapet (when two-parts is selected)
      const parapetPrice = pricing.achterdeur?.parapet?.[config.parapet] || 0;
      if (parapetPrice > 0) {
        optionItems.push({ name: `Parapet: ${config.parapet === 'with-parapet' ? 'With Parapet' : 'No Parapet'}`, price: parapetPrice });
        totalOptions += parapetPrice;
      }
      
      // Security
      const securityPrice = pricing.achterdeur?.security?.[config.security] || 0;
      if (securityPrice > 0) {
        optionItems.push({ name: `Security: ${config.security === 'skg-2' ? 'SKG 2 Rating' : config.security}`, price: securityPrice });
        totalOptions += securityPrice;
      }
      
      // Top Light Overhead Parts (when topLight is with-top-light)
      if (config.topLight === 'with-top-light' && config.overheadParts > 1) {
        const overheadPartsPrice = pricing.achterdeur?.overheadParts?.[config.overheadParts] || 0;
        if (overheadPartsPrice > 0) {
          optionItems.push({ name: `${config.overheadParts} Overhead Parts`, price: overheadPartsPrice });
          totalOptions += overheadPartsPrice;
        }
        
        // Overhead window types
        config.overheadPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          const typePrice = pricing.raamtype[type] || 0;
          optionItems.push({ name: `Overhead Part ${index + 1}: ${raamtype?.name || type}`, price: typePrice });
          totalOptions += typePrice;
        });
      }
      
      // Interior/Exterior Colors (for Achterdeur)
      const interiorColor = colors.find(c => c.id === config.interiorColor);
      const interiorColorPrice = config.interiorColor === 'white' ? 0 : (pricing.interiorFrameColor[config.interiorColor] || 0);
      if (interiorColorPrice > 0) {
        optionItems.push({ name: `Interior Color: ${interiorColor?.name || config.interiorColor}`, price: interiorColorPrice });
        totalOptions += interiorColorPrice;
      }
      
      const exteriorColor = colors.find(c => c.id === config.exteriorColor);
      const exteriorColorPrice = config.exteriorColor === 'anthracite' ? 0 : (pricing.color[config.exteriorColor] || 0);
      if (exteriorColorPrice > 0) {
        optionItems.push({ name: `Exterior Color: ${exteriorColor?.name || config.exteriorColor}`, price: exteriorColorPrice });
        totalOptions += exteriorColorPrice;
      }
    }
    // Front Door (Voordeur) specific options
    else if (config.doorStyle !== undefined) {
      // Door Style
      const doorStylePrice = pricing.voordeur?.doorStyle?.[config.doorStyle] || 0;
      if (doorStylePrice > 0) {
        const doorStyleNames = {
          'style-1': 'Style 1',
          'style-2': 'Style 2',
          'style-3': 'Style 3',
          'style-4': 'Style 4',
          'style-5': 'Style 5',
          'style-6': 'Style 6'
        };
        optionItems.push({ name: `Door Style: ${doorStyleNames[config.doorStyle] || config.doorStyle}`, price: doorStylePrice });
        totalOptions += doorStylePrice;
      }
      
      // Parapet/Handle Type
      const parapetPrice = pricing.voordeur?.parapet?.[config.parapet] || 0;
      if (parapetPrice > 0) {
        const parapetNames = {
          'standard-knob': 'Standard Door Knob',
          'rvs-bar': 'RVS Bar'
        };
        optionItems.push({ name: `Handle: ${parapetNames[config.parapet] || config.parapet}`, price: parapetPrice });
        totalOptions += parapetPrice;
      }
      
      // Threshold Height
      const thresholdPrice = pricing.voordeur?.thresholdHeight?.[config.thresholdHeight] || 0;
      if (thresholdPrice > 0) {
        optionItems.push({ name: `Threshold: ${config.thresholdHeight}`, price: thresholdPrice });
        totalOptions += thresholdPrice;
      }
      
      // Security
      const securityPrice = pricing.voordeur?.security?.[config.security] || 0;
      if (securityPrice > 0) {
        optionItems.push({ name: `Security: ${config.security === 'extra-security' ? 'Extra Security Hardware' : config.security}`, price: securityPrice });
        totalOptions += securityPrice;
      }
      
      // Interior Frame Color (for voordeur)
      const interiorColor = colors.find(c => c.id === config.interiorColor);
      const interiorColorPrice = config.interiorColor === 'white' ? 0 : (pricing.interiorFrameColor?.[config.interiorColor] || 0);
      if (interiorColorPrice > 0) {
        optionItems.push({ name: `Interior Color: ${interiorColor?.name || config.interiorColor}`, price: interiorColorPrice });
        totalOptions += interiorColorPrice;
      }
      
      // Exterior Frame Color (for voordeur)
      const exteriorColor = colors.find(c => c.id === config.exteriorColor);
      const exteriorColorPrice = config.exteriorColor === 'anthracite' ? 0 : (pricing.color?.[config.exteriorColor] || 0);
      if (exteriorColorPrice > 0) {
        optionItems.push({ name: `Exterior Color: ${exteriorColor?.name || config.exteriorColor}`, price: exteriorColorPrice });
        totalOptions += exteriorColorPrice;
      }
    }
    // Terrace Door specific options
    else if (config.designType !== undefined) {
      // Design Type
      const designTypePrice = pricing.terrasdeur?.designType?.[config.designType] || 0;
      if (designTypePrice > 0) {
        const designTypeNames = {
          'no-panel': 'No Panel',
          'left-panel': 'Left Panel',
          'right-panel': 'Right Panel',
          'both-panels': 'Both Panels'
        };
        optionItems.push({ name: `Design: ${designTypeNames[config.designType] || config.designType}`, price: designTypePrice });
        totalOptions += designTypePrice;
      }
      
      // Door Type
      const doorTypePrice = pricing.terrasdeur?.doorType?.[config.doorType] || 0;
      if (doorTypePrice > 0) {
        const doorTypeNames = {
          'normal': 'Normal',
          'top-light': 'Top Light'
        };
        optionItems.push({ name: `Door Type: ${doorTypeNames[config.doorType] || config.doorType}`, price: doorTypePrice });
        totalOptions += doorTypePrice;
      }
      
      // Threshold Height
      const thresholdPrice = pricing.terrasdeur?.thresholdHeight?.[config.thresholdHeight] || 0;
      if (thresholdPrice > 0) {
        optionItems.push({ name: `Threshold: ${config.thresholdHeight}`, price: thresholdPrice });
        totalOptions += thresholdPrice;
      }
      
      // Parapet
      const parapetPrice = pricing.terrasdeur?.parapet?.[config.parapet] || 0;
      if (parapetPrice > 0) {
        optionItems.push({ name: `Parapet: ${config.parapet === 'with-parapet' ? 'With Parapet' : 'No Parapet'}`, price: parapetPrice });
        totalOptions += parapetPrice;
      }
      
      // Security Bracket
      const securityPrice = pricing.terrasdeur?.securityBracket?.[config.securityBracket] || 0;
      if (securityPrice > 0) {
        optionItems.push({ name: `Security: ${config.securityBracket === 'skg-2' ? 'SKG 2 Rating' : config.securityBracket}`, price: securityPrice });
        totalOptions += securityPrice;
      }
      
      // Top Light Overhead Parts (when doorType is top-light)
      if (config.doorType === 'top-light' && config.overheadParts > 1) {
        const overheadPartsPrice = pricing.terrasdeur?.overheadParts?.[config.overheadParts] || 0;
        if (overheadPartsPrice > 0) {
          optionItems.push({ name: `${config.overheadParts} Overhead Parts`, price: overheadPartsPrice });
          totalOptions += overheadPartsPrice;
        }
        
        // Overhead window types
        config.overheadPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          const typePrice = pricing.raamtype[type] || 0;
          optionItems.push({ name: `Overhead Part ${index + 1}: ${raamtype?.name || type}`, price: typePrice });
          totalOptions += typePrice;
        });
      }
    } else {
      // Window configurator logic
      // Window types
      config.partWindowTypes?.forEach((type, index) => {
        const raamtype = raamtypes.find(r => r.id === type);
        const typePrice = pricing.raamtype[type] || 0;
        optionItems.push({ name: `Part ${index + 1}: ${raamtype?.name || type}`, price: typePrice });
        totalOptions += typePrice;
      });
      
      // Overhead
      if (config.overhead === 'overhead') {
        const overheadPrice = pricing.overhead.overhead;
        optionItems.push({ name: 'Overhead', price: overheadPrice });
        totalOptions += overheadPrice;
        
        config.overheadPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          const typePrice = pricing.raamtype[type] || 0;
          optionItems.push({ name: `Overhead Part ${index + 1}: ${raamtype?.name || type}`, price: typePrice });
          totalOptions += typePrice;
        });
      }
      
      // Underlight
      if (config.underlight === 'underlight') {
        const underlightPrice = pricing.underlight?.underlight || 0;
        optionItems.push({ name: 'Underlight', price: underlightPrice });
        totalOptions += underlightPrice;
        
        // Underlight parts
        if (config.underlightParts > 1) {
          const underlightPartsPrice = (config.underlightParts - 1) * (pricing.underlightPart || 0);
          if (underlightPartsPrice > 0) {
            optionItems.push({ name: `${config.underlightParts} Underlight Parts`, price: underlightPartsPrice });
            totalOptions += underlightPartsPrice;
          }
        }
        
        // Underlight window types
        config.underlightPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          const typePrice = pricing.raamtype[type] || 0;
          optionItems.push({ name: `Underlight Part ${index + 1}: ${raamtype?.name || type}`, price: typePrice });
          totalOptions += typePrice;
        });
      }
    }
    
    // Wood Joint
    const woodJoint = woodJointOptions.find(w => w.id === config.woodJoint);
    const woodJointPrice = pricing.woodJoint[config.woodJoint] || 0;
    optionItems.push({ name: `Joint: ${woodJoint?.name || config.woodJoint}`, price: woodJointPrice });
    totalOptions += woodJointPrice;
    
    // Hardware
    config.hardware?.forEach(hwId => {
      const hw = hardware.find(h => h.id === hwId);
      const hwPrice = pricing.hardware[hwId] || 0;
      if (hwPrice > 0) {
        optionItems.push({ name: `Hardware: ${hw?.name || hwId}`, price: hwPrice });
        totalOptions += hwPrice;
      }
    });
    
    // Accessories
    config.accessories?.forEach(accId => {
      const acc = accessories.find(a => a.id === accId);
      const accPrice = pricing.accessories[accId] || 0;
      if (accPrice > 0) {
        optionItems.push({ name: `Accessory: ${acc?.name || accId}`, price: accPrice });
        totalOptions += accPrice;
      }
    });
    
    return {
      basePrice: finalBasePrice,
      options: optionItems,
      totalOptions,
      totalPrice: finalBasePrice + totalOptions
    };
  };

  const prices = calculatePrices();
  
  // Read quantity - prioritize currentQuantityInput prop, then config.quantity, then currentQuantity state
  // This ensures we always get the latest value from the Summary input
  const quantity = (currentQuantityInput !== undefined && currentQuantityInput !== null && !isNaN(currentQuantityInput) && Number(currentQuantityInput) >= 1)
    ? Number(currentQuantityInput)
    : (config?.quantity !== undefined && config?.quantity !== null && !isNaN(config?.quantity) && Number(config?.quantity) >= 1)
      ? Number(config?.quantity)
      : currentQuantity;
  const formatPrice = (price) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  // Generate specification rows for the table
  const getSpecificationRows = () => {
    const specs = [];
    
    // Always include these basic specs - match Summary format
    specs.push({ name: `Width: ${config.width} mm` });
    specs.push({ name: `Height: ${config.height} mm` });
    specs.push({ name: `Area: ${((config.width * config.height) / 1000000).toFixed(2)} m²` });
    
    // Window configurator (Kozijnen)
    if (config.parts !== undefined) {
      specs.push({ name: `Number of Parts: ${config.parts}` });
    }
    
    // Always include Profile System, Glass Texture, and Glazing
    const profileSystem = profileSystems.find(p => p.id === config.profileSystem);
    if (profileSystem) {
      specs.push({ name: `Profile System: ${profileSystem.name}` });
    }
    
    // Glass Texture - handle both regular and sliding door (left/right)
    if (config.leftGlassTexture && config.rightGlassTexture) {
      // Sliding door with separate left and right glass
      const leftGlassTexture = glassTextures.find(g => g.id === config.leftGlassTexture);
      const rightGlassTexture = glassTextures.find(g => g.id === config.rightGlassTexture);
      if (leftGlassTexture) {
        specs.push({ name: `Glass Texture (Left): ${translateGlassTextureName(config.leftGlassTexture)}` });
      }
      if (rightGlassTexture) {
        specs.push({ name: `Glass Texture (Right): ${translateGlassTextureName(config.rightGlassTexture)}` });
      }
    } else {
      const glassTexture = glassTextures.find(g => g.id === config.glassTexture);
      if (glassTexture) {
        specs.push({ name: `Glass Texture: ${translateGlassTextureName(config.glassTexture)}` });
      }
    }
    
    // Glazing Type - handle both regular and sliding door (right glazing)
    if (config.rightGlazingType) {
      const rightGlazingType = glazingTypes.find(g => g.id === config.rightGlazingType);
      if (rightGlazingType) {
        specs.push({ name: `Glazing (Right): ${translateGlazingTypeName(config.rightGlazingType)}` });
      }
    } else {
      const glazingType = glazingTypes.find(g => g.id === config.glazingType);
      if (glazingType) {
        specs.push({ name: `Glazing: ${translateGlazingTypeName(config.glazingType)}` });
      }
    }
    
    // Sliding Door (Schuifpui) specific
    if (config.slidingPosition || (config.doorType === 'sliding')) {
      const slidingPositionNames = {
        'left': 'Sliding Section Left',
        'right': 'Sliding Section Right',
        'center': 'Sliding Section Center'
      };
      if (config.slidingPosition) {
        specs.push({ name: `Sliding Position: ${slidingPositionNames[config.slidingPosition] || config.slidingPosition}` });
      }
      
      if (config.thresholdHeight) {
        specs.push({ name: `Threshold: ${config.thresholdHeight}` });
      }
      
      const interiorColor = colors.find(c => c.id === config.interiorColor);
      if (interiorColor) {
        specs.push({ name: `Interior Color: ${interiorColor.name}` });
      }
      
      const exteriorColor = colors.find(c => c.id === config.exteriorColor);
      if (exteriorColor) {
        specs.push({ name: `Exterior Color: ${exteriorColor.name}` });
      }
    }
    // Front Door (Voordeur) specific
    if (config.doorStyle !== undefined) {
      const doorStyleNames = {
        'style-1': 'Style 1',
        'style-2': 'Style 2',
        'style-3': 'Style 3',
        'style-4': 'Style 4',
        'style-5': 'Style 5',
        'style-6': 'Style 6'
      };
      specs.push({ name: `Door Style: ${doorStyleNames[config.doorStyle] || config.doorStyle}` });
      
      const handleNames = {
        'standard-knob': 'Standard Door Knob',
        'rvs-bar': 'RVS Bar'
      };
      specs.push({ name: `Handle Type: ${handleNames[config.parapet] || config.parapet}` });
      specs.push({ name: `Handle Position: ${config.handlePosition === 'left' ? 'Left' : config.handlePosition === 'right' ? 'Right' : config.handlePosition}` });
      specs.push({ name: `Threshold: ${config.thresholdHeight}` });
      
      if (config.security && config.security !== 'none') {
        specs.push({ name: `Security: ${config.security === 'extra-security' ? 'Extra Security Hardware' : config.security}` });
      }
      
      const interiorColor = colors.find(c => c.id === config.interiorColor);
      if (interiorColor) {
        specs.push({ name: `Interior Color: ${interiorColor.name}` });
      }
      
      const exteriorColor = colors.find(c => c.id === config.exteriorColor);
      if (exteriorColor) {
        specs.push({ name: `Exterior Color: ${exteriorColor.name}` });
      }
    }
    // Back Door (Achterdeur) specific
    else if (config.topLight !== undefined || config.numberOfParts !== undefined) {
      specs.push({ name: `Top Light: ${config.topLight === 'no-top-light' ? 'No Top Light' : config.topLight === 'with-top-light' ? 'With Top Light' : config.topLight}` });
      specs.push({ name: `Number of Parts: ${config.numberOfParts === 'one-part' ? 'One Part' : config.numberOfParts === 'two-parts' ? 'Two Parts' : config.numberOfParts}` });
      
      if (config.numberOfParts === 'two-parts' && config.panelType) {
        const panelTypeNames = {
          'sandwich-no-mullions': 'Sandwich Panel (No Mullions)',
          'glass': 'Glass',
          'horizontal-mullions': 'Horizontal Mullions',
          'vertical-mullions': 'Vertical Mullions'
        };
        specs.push({ name: `Panel Type: ${panelTypeNames[config.panelType] || config.panelType}` });
      }
      
      specs.push({ name: `Handle Position: ${config.handlePosition === 'left' ? 'Left' : config.handlePosition === 'right' ? 'Right' : config.handlePosition}` });
      specs.push({ name: `Threshold: ${config.thresholdHeight}` });
      
      if (config.parapet === 'with-parapet') {
        specs.push({ name: `Parapet Height: ${config.parapetHeight} mm` });
      }
      
      if (config.security && config.security !== 'none') {
        specs.push({ name: `Security: ${config.security === 'skg-2' ? 'SKG 2 Rating' : config.security}` });
      }
      
      const interiorColor = colors.find(c => c.id === config.interiorColor);
      if (interiorColor) {
        specs.push({ name: `Interior Color: ${interiorColor.name}` });
      }
      
      const exteriorColor = colors.find(c => c.id === config.exteriorColor);
      if (exteriorColor) {
        specs.push({ name: `Exterior Color: ${exteriorColor.name}` });
      }
      
      if (config.topLight === 'with-top-light' && config.overheadParts) {
        specs.push({ name: `Overhead Parts: ${config.overheadParts}` });
      }
    }
    // Terrace Door (Terrasdeur) specific
    else if (config.designType !== undefined) {
      const designTypeNames = {
        'no-panel': 'No Panel',
        'left-panel': 'Left Panel',
        'right-panel': 'Right Panel',
        'both-panels': 'Both Panels'
      };
      specs.push({ name: `Design Type: ${designTypeNames[config.designType] || config.designType}` });
      specs.push({ name: `Door Type: ${config.doorType === 'normal' ? 'Normal' : config.doorType === 'top-light' ? 'Top Light' : config.doorType}` });
      specs.push({ name: `Handle Position: ${config.handlePosition === 'left' ? 'Left' : config.handlePosition === 'right' ? 'Right' : config.handlePosition}` });
      specs.push({ name: `Threshold: ${config.thresholdHeight}` });
      
      if (config.parapet === 'with-parapet') {
        specs.push({ name: `Parapet Height: ${config.parapetHeight} mm` });
      }
      
      if (config.securityBracket !== 'none') {
        specs.push({ name: `Security: ${config.securityBracket === 'skg-2' ? 'SKG 2 Rating' : config.securityBracket}` });
      }
      
      if (config.doorType === 'top-light' && config.overheadParts) {
        specs.push({ name: `Overhead Parts: ${config.overheadParts}` });
      }
      
      const interiorColor = colors.find(c => c.id === config.interiorColor);
      if (interiorColor) {
        specs.push({ name: `Interior Color: ${interiorColor.name}` });
      }
      
      const exteriorColor = colors.find(c => c.id === config.exteriorColor);
      if (exteriorColor) {
        specs.push({ name: `Exterior Color: ${exteriorColor.name}` });
      }
    }
    
    return specs;
  };

  const specificationRows = getSpecificationRows();

  // Generate description items from config (similar to Summary component)
  const getDescriptionItems = () => {
    const items = [];
    
    // Dimensions - add at the beginning to match Summary format
    items.push(`- Width: ${config.width} mm`);
    items.push(`- Height: ${config.height} mm`);
    items.push(`- Area: ${((config.width * config.height) / 1000000).toFixed(2)} m²`);
    
    // Detect page type from config
    const pageType = config.doorStyle !== undefined ? 'voordeur' :
                     config.designType !== undefined ? 'terrasdeur' :
                     config.topLight !== undefined || config.numberOfParts !== undefined ? 'achterdeur' :
                     config.doorType === 'sliding' || config.slidingPosition ? 'schuifpui' :
                     'kozijnen';
    
    // Profile System
    if (profileSystems && profileSystems.length > 0 && config.profileSystem) {
      const profileSystem = profileSystems.find(p => p.id === config.profileSystem);
      if (profileSystem) {
        items.push(`- ${profileSystem.name} frames`);
      }
    }
    
    // External Frame Color
    const externalColorId = config.exteriorColor || config.color;
    if (externalColorId) {
      const colorData = getColorById(externalColorId);
      const colorName = colorData?.name || (colorData?.ral ? `RAL ${colorData.ral}` : externalColorId);
      items.push(`- Exterior frame ${colorName}`);
    }
    
    // Interior Frame Color (for windows)
    const hasInteriorFrames = config.partWindowTypes?.some(type => 
      ['fixed-glass', 'tilt-only', 'tilt-turn-left', 'tilt-turn-right'].includes(type)
    ) || config.overheadPartWindowTypes?.some(type => 
      ['fixed-glass', 'tilt-only', 'tilt-turn-left', 'tilt-turn-right'].includes(type)
    );
    
    if (hasInteriorFrames && config.interiorFrameColor) {
      const interiorColorData = getColorById(config.interiorFrameColor);
      const interiorColorName = interiorColorData?.name || (interiorColorData?.ral ? `RAL ${interiorColorData.ral}` : config.interiorFrameColor);
      items.push(`- Interior frame ${interiorColorName}`);
    }
    
    // Interior Color (for doors - Schuifpui, Achterdeur, Voordeur, Terrasdeur)
    if (config.interiorColor && (pageType === 'schuifpui' || pageType === 'achterdeur' || pageType === 'voordeur' || pageType === 'terrasdeur')) {
      const interiorColorData = getColorById(config.interiorColor);
      const interiorColorName = interiorColorData?.name || (interiorColorData?.ral ? `RAL ${interiorColorData.ral}` : config.interiorColor);
      items.push(`- Interior frame ${interiorColorName}`);
    }
    
    // Glass Texture
    if (config.glassTexture) {
      const glassTexture = glassTextures.find(g => g.id === config.glassTexture);
      if (glassTexture) {
        items.push(`- ${translateGlassTextureName(config.glassTexture)} glass`);
      }
    }
    
    // Glazing Type
    if (config.glazingType) {
      const glazingType = glazingTypes.find(g => g.id === config.glazingType);
      if (glazingType) {
        items.push(`- ${translateGlazingTypeName(config.glazingType)}`);
      }
    }
    
    // Sliding Door (Schuifpui) specific options
    if (pageType === 'schuifpui' || config.doorType === 'sliding') {
      // Sliding Position
      if (config.slidingPosition) {
        const slidingPositionNames = {
          'left': 'Sliding Section Left',
          'right': 'Sliding Section Right',
          'center': 'Sliding Section Center'
        };
        items.push(`- Sliding Position: ${slidingPositionNames[config.slidingPosition] || config.slidingPosition}`);
      }
      
      // Threshold Height
      if (config.thresholdHeight) {
        items.push(`- Threshold: ${config.thresholdHeight}`);
      }
      
      // Left Glass Texture
      if (config.leftGlassTexture) {
        const leftGlassTexture = glassTextures.find(g => g.id === config.leftGlassTexture);
        if (leftGlassTexture) {
          items.push(`- Glass (Left): ${translateGlassTextureName(config.leftGlassTexture)}`);
        }
      }
      
      // Right Glass Texture
      if (config.rightGlassTexture) {
        const rightGlassTexture = glassTextures.find(g => g.id === config.rightGlassTexture);
        if (rightGlassTexture) {
          items.push(`- Glass (Right): ${translateGlassTextureName(config.rightGlassTexture)}`);
        }
      }
      
      // Right Glazing Type
      if (config.rightGlazingType) {
        const rightGlazingType = glazingTypes.find(g => g.id === config.rightGlazingType);
        if (rightGlazingType) {
          items.push(`- Glazing (Right): ${translateGlazingTypeName(config.rightGlazingType)}`);
        }
      }
    }
    
    // Back Door (Achterdeur) specific options
    if (pageType === 'achterdeur') {
      // Top Light
      if (config.topLight) {
        const topLightNames = {
          'no-top-light': 'No Top Light',
          'with-top-light': 'With Top Light'
        };
        items.push(`- Top Light: ${topLightNames[config.topLight] || config.topLight}`);
      }
      
      // Number of Parts
      if (config.numberOfParts) {
        const numberOfPartsNames = {
          'one-part': 'One Part',
          'two-parts': 'Two Parts'
        };
        items.push(`- Parts: ${numberOfPartsNames[config.numberOfParts] || config.numberOfParts}`);
      }
      
      // Panel Type (only when two-parts is selected)
      if (config.numberOfParts === 'two-parts' && config.panelType) {
        const panelTypeNames = {
          'sandwich-no-mullions': 'Sandwich Panel (No Mullions)',
          'glass': 'Glass',
          'horizontal-mullions': 'Horizontal Mullions',
          'vertical-mullions': 'Vertical Mullions'
        };
        items.push(`- Panel Type: ${panelTypeNames[config.panelType] || config.panelType}`);
      }
      
      // Threshold Height
      if (config.thresholdHeight) {
        items.push(`- Threshold: ${config.thresholdHeight}`);
      }
      
      // Parapet
      if (config.parapet) {
        items.push(`- Parapet: ${config.parapet === 'with-parapet' ? 'With Parapet' : 'No Parapet'}`);
      }
      
      // Security - always show as SKG 2 Rating
      items.push(`- Security: SKG 2 Rating`);
      
      // Top Light Overhead Parts
      if (config.topLight === 'with-top-light' && config.overheadParts > 1) {
        items.push(`- ${config.overheadParts} Overhead Parts`);
        
        // Overhead window types
        config.overheadPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            items.push(`- Overhead Part ${index + 1}: ${raamtype.name}`);
          }
        });
      }
    }
    
    // Front Door (Voordeur) specific options
    if (pageType === 'voordeur') {
      // Door Style
      if (config.doorStyle) {
        const doorStyleNames = {
          'style-1': 'Style 1',
          'style-2': 'Style 2',
          'style-3': 'Style 3',
          'style-4': 'Style 4',
          'style-5': 'Style 5',
          'style-6': 'Style 6'
        };
        items.push(`- Door Style: ${doorStyleNames[config.doorStyle] || config.doorStyle}`);
      }
      
      // Parapet/Handle Type
      if (config.parapet) {
        const parapetNames = {
          'standard-knob': 'Standard Door Knob',
          'rvs-bar': 'RVS Bar'
        };
        items.push(`- Handle: ${parapetNames[config.parapet] || config.parapet}`);
      }
      
      // Threshold Height
      if (config.thresholdHeight) {
        items.push(`- Threshold: ${config.thresholdHeight}`);
      }
      
      // Security - always show as SKG 2 Rating
      items.push(`- Security: SKG 2 Rating`);
    }
    
    // Terrace Door (Terrasdeur) specific options
    if (pageType === 'terrasdeur') {
      // Design Type
      if (config.designType) {
        const designTypeNames = {
          'no-panel': 'No Panel',
          'left-panel': 'Left Panel',
          'right-panel': 'Right Panel',
          'both-panels': 'Both Panels'
        };
        items.push(`- Design: ${designTypeNames[config.designType] || config.designType}`);
      }
      
      // Door Type
      if (config.doorType) {
        const doorTypeNames = {
          'normal': 'Normal',
          'top-light': 'Top Light'
        };
        items.push(`- Door Type: ${doorTypeNames[config.doorType] || config.doorType}`);
      }
      
      // Handle Position
      if (config.handlePosition) {
        const handlePositionNames = {
          'left': 'Handle Left',
          'right': 'Handle Right'
        };
        items.push(`- Handle Position: ${handlePositionNames[config.handlePosition] || config.handlePosition}`);
      }
      
      // Threshold Height
      if (config.thresholdHeight) {
        items.push(`- Threshold: ${config.thresholdHeight}`);
      }
      
      // Parapet
      if (config.parapet) {
        items.push(`- Parapet: ${config.parapet === 'with-parapet' ? 'With Parapet' : 'No Parapet'}`);
      }
      
      // Security Bracket - always show as SKG 2 Rating
      items.push(`- Security: SKG 2 Rating`);
      
      // Top Light Overhead Parts
      if (config.doorType === 'top-light' && config.overheadParts > 1) {
        items.push(`- ${config.overheadParts} Overhead Parts`);
        
        // Overhead window types
        config.overheadPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            items.push(`- Overhead Part ${index + 1}: ${raamtype.name}`);
          }
        });
      }
    }
    
    // Window configurator (Kozijnen) specific options
    if (pageType === 'kozijnen') {
      // Division Type
      if (config.divisionType) {
        items.push(`- Division: ${config.divisionType}`);
      }
      
      // Window types per part
      if (config.partWindowTypes && config.partWindowTypes.length > 0) {
        config.partWindowTypes.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            if (type === 'fixed-panel') {
              let panelInfo = [];
              const panelColorId = config.panelColors?.[index];
              if (panelColorId) {
                const panelColorData = getColorById(panelColorId);
                const panelColorName = panelColorData?.name || (panelColorData?.ral ? `RAL ${panelColorData.ral}` : panelColorId);
                panelInfo.push(panelColorName);
              } else {
                panelInfo.push('Anthracite Grey (RAL 7016)');
              }
              const panelLineType = config.panelLineTypes?.[index] || 'none';
              if (panelLineType === 'horizontal') {
                panelInfo.push('Horizontal Lines');
              } else if (panelLineType === 'vertical') {
                panelInfo.push('Vertical Lines');
              } else {
                panelInfo.push('Plain');
              }
              const panelInfoStr = panelInfo.length > 0 ? ` - ${panelInfo.join(' - ')}` : '';
              items.push(`- Part ${index + 1}: ${raamtype.name}${panelInfoStr}`);
            } else {
              items.push(`- Part ${index + 1}: ${raamtype.name}`);
            }
          }
        });
      }
      
      // Overhead
      if (config.overhead === 'overhead') {
        items.push('- Overhead');
        
        if (config.overheadParts > 1) {
          items.push(`- ${config.overheadParts} Overhead Parts`);
        }
        
        // Overhead window types
        config.overheadPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            if (type === 'fixed-panel') {
              let panelInfo = [];
              const panelColorId = config.panelOverheadColors?.[index];
              if (panelColorId) {
                const panelColorData = getColorById(panelColorId);
                const panelColorName = panelColorData?.name || (panelColorData?.ral ? `RAL ${panelColorData.ral}` : panelColorId);
                panelInfo.push(panelColorName);
              } else {
                panelInfo.push('Anthracite Grey (RAL 7016)');
              }
              const panelLineType = config.panelOverheadLineTypes?.[index] || 'none';
              if (panelLineType === 'horizontal') {
                panelInfo.push('Horizontal Lines');
              } else if (panelLineType === 'vertical') {
                panelInfo.push('Vertical Lines');
              } else {
                panelInfo.push('Plain');
              }
              const panelInfoStr = panelInfo.length > 0 ? ` - ${panelInfo.join(' - ')}` : '';
              items.push(`- Overhead Part ${index + 1}: ${raamtype.name}${panelInfoStr}`);
            } else {
              items.push(`- Overhead Part ${index + 1}: ${raamtype.name}`);
            }
          }
        });
      }
      
      // Underlight
      if (config.underlight === 'underlight') {
        items.push('- Underlight');
        
        if (config.underlightParts > 1) {
          items.push(`- ${config.underlightParts} Underlight Parts`);
        }
        
        // Underlight window types
        config.underlightPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            if (type === 'fixed-panel') {
              let panelInfo = [];
              const panelColorId = config.panelUnderlightColors?.[index];
              if (panelColorId) {
                const panelColorData = getColorById(panelColorId);
                const panelColorName = panelColorData?.name || (panelColorData?.ral ? `RAL ${panelColorData.ral}` : panelColorId);
                panelInfo.push(panelColorName);
              } else {
                panelInfo.push('Anthracite Grey (RAL 7016)');
              }
              const panelLineType = config.panelUnderlightLineTypes?.[index] || 'none';
              if (panelLineType === 'horizontal') {
                panelInfo.push('Horizontal Lines');
              } else if (panelLineType === 'vertical') {
                panelInfo.push('Vertical Lines');
              } else {
                panelInfo.push('Plain');
              }
              const panelInfoStr = panelInfo.length > 0 ? ` - ${panelInfo.join(' - ')}` : '';
              items.push(`- Underlight Part ${index + 1}: ${raamtype.name}${panelInfoStr}`);
            } else {
              items.push(`- Underlight Part ${index + 1}: ${raamtype.name}`);
            }
          }
        });
      }
    }
    
    // Wood Joint
    if (config.woodJoint && woodJointOptions && woodJointOptions.length > 0) {
      const woodJoint = woodJointOptions.find(w => w.id === config.woodJoint);
      if (woodJoint) {
        items.push(`- Joint: ${woodJoint.name}`);
      }
    }
    
    // Hardware
    if (config.hardware && Array.isArray(config.hardware) && hardware && hardware.length > 0) {
      config.hardware.forEach(hwId => {
        if (hwId) {
          const hw = hardware.find(h => h.id === hwId);
          if (hw) {
            items.push(`- Hardware: ${hw.name}`);
          }
        }
      });
    }
    
    // Accessories
    if (config.accessories && Array.isArray(config.accessories) && accessories && accessories.length > 0) {
      config.accessories.forEach(accId => {
        if (accId) {
          const acc = accessories.find(a => a.id === accId);
          if (acc) {
            items.push(`- Accessory: ${acc.name}`);
          }
        }
      });
    }
    
    // Additional Options
    if (config.screenElectricallyOperated === true) {
      const screenColorData = getColorByRAL(config.screenColor || '7016');
      const screenColorName = screenColorData?.ral ? `RAL ${screenColorData.ral.replace(/^0+/, '')}` : 'RAL 7016';
      items.push(`- Screen electrically operated (${screenColorName})`);
    }
    
    if (config.rollerShutterElectricallyOperated === true) {
      const shutterColorData = getColorByRAL(config.rollerShutterColor || '7016');
      const shutterColorName = shutterColorData?.ral ? `RAL ${shutterColorData.ral.replace(/^0+/, '')}` : 'RAL 7016';
      items.push(`- Roller shutter electrically operated (${shutterColorName})`);
    }
    
    if (config.hor === true) {
      const horColorData = getColorByRAL(config.horColor || '7016');
      const horColorName = horColorData?.ral ? `RAL ${horColorData.ral.replace(/^0+/, '')}` : 'RAL 7016';
      items.push(`- Hor (${horColorName})`);
    }
    
    if (config.buoySharing === true) {
      const buoyColorData = getColorByRAL(config.buoySharingColor || '7016');
      const buoyColorName = buoyColorData?.ral ? `RAL ${buoyColorData.ral.replace(/^0+/, '')}` : 'RAL 7016';
      const mmText = config.buoySharingMm ? `, ${config.buoySharingMm}mm` : '';
      items.push(`- ${t('quote.buoySharing')} (${buoyColorName}${mmText})`);
    }
    
    // Standard included items
    items.push('- Installation');
    items.push('- Standard interior frame finishing');
    items.push('- Removal of old materials');
    
    return items;
  };

  const descriptionItems = getDescriptionItems();

  const handleExportPDF = async () => {
    if (!quoteRef.current) return;

    // Prevent export if form is still in editing mode or hasn't been saved
    if (isEditing || !isSaved) {
      alert(t('quote.pleaseSaveBeforeExport'));
      return;
    }

    try {
      // Hide edit buttons and action buttons before capturing
      const editButtons = quoteRef.current.querySelectorAll('button.print\\:hidden, .print\\:hidden button, .print\\:hidden');
      const actionButtons = quoteRef.current.parentElement?.querySelectorAll('button') || [];
      const allButtonsToHide = [...editButtons, ...actionButtons];
      const originalDisplays = [];
      allButtonsToHide.forEach((btn, index) => {
        originalDisplays[index] = window.getComputedStyle(btn).display;
        btn.style.display = 'none';
      });

      // Increase font sizes for PDF by adding a temporary class
      quoteRef.current.classList.add('pdf-export');
      const style = document.createElement('style');
      style.textContent = `
        .pdf-export { font-size: 1.4em !important; }
        .pdf-export .text-xs { font-size: 0.9rem !important; }
        .pdf-export .text-sm { font-size: 1.1rem !important; }
        .pdf-export .text-base { font-size: 1.3rem !important; }
        .pdf-export .text-xl { font-size: 1.8rem !important; }
        .pdf-export .text-2xl { font-size: 2.2rem !important; }
        .pdf-export h1 { font-size: 2.2rem !important; }
        .pdf-export h2 { font-size: 1.8rem !important; }
        .pdf-export h3 { font-size: 1.3rem !important; }
        .pdf-export p { font-size: 0.9rem !important; }
        .pdf-export table { font-size: 1rem !important; }
        .pdf-export th, .pdf-export td { font-size: 1rem !important; padding: 0.75rem 1rem !important; }
      `;
      document.head.appendChild(style);

      // Wait a moment for styles to apply
      await new Promise(resolve => setTimeout(resolve, 100));

      // Capture at high resolution for quality
      const canvas = await html2canvas(quoteRef.current, {
        scale: 2, // Scale for quality
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        allowTaint: true,
        width: quoteRef.current.scrollWidth,
        height: quoteRef.current.scrollHeight
      });

      // Remove the temporary class and style
      quoteRef.current.classList.remove('pdf-export');
      document.head.removeChild(style);

      // Restore edit buttons after capturing
      allButtonsToHide.forEach((btn, index) => {
        btn.style.display = originalDisplays[index];
      });

      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth(); // 210mm for A4
      const pdfHeight = pdf.internal.pageSize.getHeight(); // 297mm for A4
      
      // Convert pixels to mm
      // html2canvas uses 96 DPI by default, so 1px = 25.4/96 mm
      // At scale 2, the canvas is 2x larger, so we divide by 2
      const pxToMm = (25.4 / 96) / 2;
      const imgWidthMm = canvas.width * pxToMm;
      const imgHeightMm = canvas.height * pxToMm;
      
      // Use minimal margins to maximize content area (5mm top/bottom, 3mm sides)
      const marginTop = 5;
      const marginBottom = 5;
      const marginSides = 3;
      const availableWidth = pdfWidth - (marginSides * 2); // ~204mm
      const availableHeight = pdfHeight - marginTop - marginBottom; // ~287mm
      
      // Calculate ratios to fill the page
      const widthRatio = availableWidth / imgWidthMm;
      const heightRatio = availableHeight / imgHeightMm;
      
      // Use the smaller ratio to ensure content fits both width and height
      // This ensures content fills the entire page from top to bottom
      const ratio = Math.min(widthRatio, heightRatio);
      
      const imgScaledWidth = imgWidthMm * ratio;
      const imgScaledHeight = imgHeightMm * ratio;
      
      // Center horizontally, but start from top margin
      const xOffset = (pdfWidth - imgScaledWidth) / 2;
      const yOffset = marginTop; // Start from top margin

      pdf.addImage(imgData, 'PNG', xOffset, yOffset, imgScaledWidth, imgScaledHeight);

      // Open PDF in new tab instead of downloading
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
      
      // Clean up the blob URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 100);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('An error occurred while generating the PDF.');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Quote</DialogTitle>
        </DialogHeader>
        
        {/* Main quote content. Extra bottom padding so pricing table never touches the action bar. */}
        <div
          ref={quoteRef}
          className="bg-white p-6 pb-28 print:p-4"
          id="quote-content"
          style={{ maxHeight: '297mm', overflow: 'visible', width: '100%' }}
        >
          {/* Company Header - Full Width */}
          <div className="mb-4 border-b-2 border-gray-300 pb-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">bestonline Kozijnen</h1>
                <p className="text-sm text-gray-700">Custom Plastic Frames</p>
              </div>
              <div className="text-right text-xs text-gray-700">
                <p>Binnenhavenstraat 41A</p>
                <p>7553 GH Hengelo</p>
                <p>Tel: +31 (0)6 33 03 30 82</p>
                <p>Email: Info@vmkkozijnen.nl</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-700">
                <p><strong>{t('quote.quoteLabel')}:</strong> {quoteNumber || 'OV-' + new Date().getFullYear() + String(Math.floor(Math.random() * 1000000)).padStart(6, '0')}</p>
                <p><strong>{t('quote.date')}:</strong> {formatDate(new Date())}</p>
                <p><strong>{t('quote.validUntil')}:</strong> {formatDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))}</p>
              </div>
            </div>
          </div>

          {/* Client Address Section - Just Before Quote Title */}
          <div className="mb-4">
            {isEditing ? (
              <div className="space-y-1">
                <Label htmlFor="clientAddress" className="text-xs">{t('quote.clientAddress')} *</Label>
                <Input
                  id="clientAddress"
                  value={clientDetails.address || ''}
                  onChange={(e) => setClientDetails({...clientDetails, address: e.target.value})}
                  placeholder={t('quote.streetAddress')}
                  className="h-7 text-xs"
                />
                <div className="grid grid-cols-2 gap-1">
                  <div>
                    <Label htmlFor="clientPostalCode" className="text-xs">{t('quote.postalCode')} *</Label>
                    <Input
                      id="clientPostalCode"
                      value={clientDetails.postalCode || ''}
                      onChange={(e) => setClientDetails({...clientDetails, postalCode: e.target.value})}
                      placeholder="1234 AB"
                      className="h-7 text-xs"
                    />
                  </div>
                  <div>
                    <Label htmlFor="clientCity" className="text-xs">{t('quote.city')} *</Label>
                    <Input
                      id="clientCity"
                      value={clientDetails.city || ''}
                      onChange={(e) => setClientDetails({...clientDetails, city: e.target.value})}
                      placeholder={t('quote.city')}
                      className="h-7 text-xs"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  <div>
                    <Label htmlFor="clientPhone" className="text-xs">{t('quote.phone')}</Label>
                    <Input
                      id="clientPhone"
                      type="tel"
                      value={clientDetails.phone || ''}
                      onChange={(e) => setClientDetails({...clientDetails, phone: e.target.value})}
                      placeholder={t('quote.phone')}
                      className="h-7 text-xs"
                    />
                  </div>
                  <div>
                    <Label htmlFor="clientEmail" className="text-xs">{t('quote.email')}</Label>
                    <Input
                      id="clientEmail"
                      type="email"
                      value={clientDetails.email || ''}
                      onChange={(e) => setClientDetails({...clientDetails, email: e.target.value})}
                      placeholder={t('quote.email')}
                      className="h-7 text-xs"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-xs text-gray-700">
                {clientDetails.address || clientDetails.postalCode || clientDetails.city || clientDetails.phone || clientDetails.email ? (
                  <>
                    {clientDetails.address && <p>{clientDetails.address}</p>}
                    {(clientDetails.postalCode || clientDetails.city) && (
                      <p>{clientDetails.postalCode} {clientDetails.city}</p>
                    )}
                    {clientDetails.phone && <p>{t('quote.phone')}: {clientDetails.phone}</p>}
                    {clientDetails.email && <p>{t('quote.email')}: {clientDetails.email}</p>}
                  </>
                ) : (
                  <p className="text-gray-400 italic">{t('quote.clientAddress')}</p>
                )}
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-1 text-orange-600 hover:text-orange-700 print:hidden text-xs mt-1"
                    title={t('quote.editAddress')}
                  >
                    <Pencil className="w-3 h-3" />
                    <span>{t('quote.editAddress')}</span>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Additional Details Section */}
          <div className="mb-6 border-t pt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-800">{t('quote.additionalDetails')}</h3>
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-1 text-orange-600 hover:text-orange-700 print:hidden text-xs"
                  title={t('quote.editAdditionalDetails')}
                >
                  <Pencil className="w-3 h-3" />
                  <span>{t('quote.edit')}</span>
                </button>
              )}
            </div>
            
            {isEditing ? (
              <>
                {/* Home Finishing */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold mb-2 text-gray-700">{t('quote.homeFinishing')}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <YesNoToggle
                      label={t('quote.typesetting')}
                      value={clientDetails.typesetting}
                      onChange={(val) => setClientDetails({...clientDetails, typesetting: val})}
                    />
                    <YesNoToggle
                      label={t('quote.container')}
                      value={clientDetails.container}
                      onChange={(val) => setClientDetails({...clientDetails, container: val})}
                    />
                    <YesNoToggle
                      label={t('quote.interiorFinish')}
                      value={clientDetails.interiorFinish}
                      onChange={(val) => setClientDetails({...clientDetails, interiorFinish: val})}
                    />
                  </div>
                </div>

                {/* Lifting Support */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold mb-2 text-gray-700">{t('quote.liftingSupport')}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <YesNoToggle
                      label={t('quote.crane')}
                      value={clientDetails.crane}
                      onChange={(val) => setClientDetails({...clientDetails, crane: val, craneTrips: val ? clientDetails.craneTrips : ''})}
                    />
                    <YesNoToggle
                      label={t('quote.liftingAssistance')}
                      value={clientDetails.liftingAssistance}
                      onChange={(val) => setClientDetails({...clientDetails, liftingAssistance: val, liftingAssistancePersonnel: val ? clientDetails.liftingAssistancePersonnel : ''})}
                    />
                  </div>
                  {/* Crane trips input - only when crane is YES */}
                  {clientDetails.crane === true && (
                    <div className="mt-2">
                      <Label htmlFor="craneTrips" className="text-xs font-semibold mb-1 block text-gray-700">Number of trips</Label>
                      <Input
                        id="craneTrips"
                        type="number"
                        min="1"
                        value={clientDetails.craneTrips || ''}
                        onChange={(e) => setClientDetails({...clientDetails, craneTrips: e.target.value})}
                        className="w-full"
                        placeholder="Enter number of trips"
                      />
                    </div>
                  )}
                  {/* Lifting assistance personnel input - only when lifting assistance is YES */}
                  {clientDetails.liftingAssistance === true && (
                    <div className="mt-2">
                      <Label htmlFor="liftingAssistancePersonnel" className="text-xs font-semibold mb-1 block text-gray-700">Personnel (Max 3)</Label>
                      <Input
                        id="liftingAssistancePersonnel"
                        type="number"
                        min="1"
                        max="3"
                        value={clientDetails.liftingAssistancePersonnel || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 3)) {
                            setClientDetails({...clientDetails, liftingAssistancePersonnel: value});
                          }
                        }}
                        className="w-full"
                        placeholder="Enter number of personnel (1-3)"
                      />
                    </div>
                  )}
                </div>

                {/* Comments */}
                <div className="mb-4">
                  <Label htmlFor="comments" className="text-xs font-semibold mb-2 block text-gray-700">{t('quote.comments')}</Label>
                  <Textarea
                    id="comments"
                    value={clientDetails.comments || ''}
                    onChange={(e) => setClientDetails({...clientDetails, comments: e.target.value})}
                    placeholder={t('quote.commentsPlaceholder')}
                    className="text-xs min-h-[80px]"
                    rows={4}
                  />
                </div>
              </>
            ) : (
              <div className="text-xs text-gray-700 space-y-3">

                {/* Home Finishing Display */}
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-gray-700">{t('quote.homeFinishing')}</h4>
                  <div className="space-y-1">
                    <p className="flex justify-between">
                      <span>{t('quote.typesetting')}:</span>
                      <span className={`font-medium ${clientDetails.typesetting ? 'text-green-600' : 'text-red-600'}`}>
                        {clientDetails.typesetting ? 'YES' : 'NO'}
                      </span>
                    </p>
                    <p className="flex justify-between">
                      <span>{t('quote.container')}:</span>
                      <span className={`font-medium ${clientDetails.container ? 'text-green-600' : 'text-red-600'}`}>
                        {clientDetails.container ? 'YES' : 'NO'}
                      </span>
                    </p>
                    <p className="flex justify-between">
                      <span>{t('quote.interiorFinish')}:</span>
                      <span className={`font-medium ${clientDetails.interiorFinish ? 'text-green-600' : 'text-red-600'}`}>
                        {clientDetails.interiorFinish ? 'YES' : 'NO'}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Lifting Support Display */}
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-gray-700">{t('quote.liftingSupport')}</h4>
                  <div className="space-y-1">
                    <p className="flex justify-between">
                      <span>{t('quote.crane')}:</span>
                      <span className={`font-medium ${clientDetails.crane ? 'text-green-600' : 'text-red-600'}`}>
                        {clientDetails.crane ? 'YES' : 'NO'}
                      </span>
                    </p>
                    {clientDetails.crane === true && clientDetails.craneTrips && (
                      <p className="flex justify-between ml-4 text-xs">
                        <span>Number of trips:</span>
                        <span className="font-medium">{clientDetails.craneTrips}</span>
                      </p>
                    )}
                    <p className="flex justify-between">
                      <span>{t('quote.liftingAssistance')}:</span>
                      <span className={`font-medium ${clientDetails.liftingAssistance ? 'text-green-600' : 'text-red-600'}`}>
                        {clientDetails.liftingAssistance ? 'YES' : 'NO'}
                      </span>
                    </p>
                    {clientDetails.liftingAssistance === true && clientDetails.liftingAssistancePersonnel && (
                      <p className="flex justify-between ml-4 text-xs">
                        <span>Personnel:</span>
                        <span className="font-medium">{clientDetails.liftingAssistancePersonnel}</span>
                      </p>
                    )}
                  </div>
                </div>

                {/* Comments Display */}
                {clientDetails.comments && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2 text-gray-700">{t('quote.comments')}</h4>
                    <p className="whitespace-pre-wrap bg-gray-50 p-2 rounded border border-gray-200">{clientDetails.comments}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Letter/Description Section */}
          <div className="mb-6 text-sm text-gray-800 leading-relaxed">
            <h2 className="text-lg font-bold mb-3">{t('quote.title')}</h2>
            
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-1">
                <Label htmlFor="clientName" className="text-xs">{t('quote.dearSirMadam')} *</Label>
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-1 text-orange-600 hover:text-orange-700 print:hidden text-xs"
                    title={t('quote.editName')}
                  >
                    <Pencil className="w-3 h-3" />
                    <span>{t('quote.edit')}</span>
                  </button>
                )}
              </div>
              {isEditing ? (
                <Input
                  id="clientName"
                  value={clientDetails.name || ''}
                  onChange={(e) => setClientDetails({...clientDetails, name: e.target.value})}
                  placeholder={t('quote.name')}
                  className="mb-3 h-8 text-sm"
                />
              ) : (
                <p className="mb-3">
                  <strong>{t('quote.dearSirMadam')} {clientDetails.name || `[${t('quote.name')}]`},</strong>
                </p>
              )}
            </div>

            <div className="space-y-3 mb-4">
              <p>
                {t('quote.thankYouMessage')}
              </p>
              <p>
                {t('quote.basedOnVisit')}
              </p>
            </div>

            <div className="mb-4">
              <h3 className="font-bold mb-2">{t('quote.ourApproach')}</h3>
              <p className="mb-2">
                {t('quote.approachDescription')}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>{t('quote.customMade')}</strong></li>
                <li><strong>{t('quote.professionalInstallation')}</strong></li>
              </ul>
            </div>

            <div className="mb-4">
              <h3 className="font-bold mb-2">{t('quote.benefits')}</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>{t('quote.benefit1')}</li>
                <li>{t('quote.benefit2')}</li>
                <li>{t('quote.benefit3')}</li>
                <li>{t('quote.benefit4')}</li>
              </ul>
            </div>

            <div className="mb-4">
              <h3 className="font-bold mb-2">{t('quote.warranty')}</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>{t('quote.warranty1')}</li>
                <li>{t('quote.warranty2')}</li>
                <li>{t('quote.warranty3')}</li>
                <li>{t('quote.warranty4')}</li>
              </ul>
              <p className="mt-2">
                <strong>{t('quote.policeCertification')}</strong>
              </p>
            </div>

          </div>

          {/* Pricing Table with Drawing */}
          <div className="mb-6">
            <table className="w-full border-collapse border border-gray-300 text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{t('quote.description')}</th>
                  <th className="border border-gray-300 px-3 py-2 text-right font-semibold">{t('quote.amount')}</th>
                  <th className="border border-gray-300 px-3 py-2 text-right font-semibold">{t('common.total')}</th>
                  <th className="border border-gray-300 px-3 py-2 text-right font-semibold">{t('common.vat')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-3 py-2" colSpan="4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="font-semibold mb-1">
                          {quantity} x Plastic frames
                          {config.parts && config.parts > 1 ? ' (Multiple Parts)' : ''}
                        </div>
                        <div className="text-xs space-y-0.5 mt-1">
                          <div>{t('quote.including')}</div>
                          {descriptionItems.map((item, index) => (
                            <div key={index}>{item}</div>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-col items-center justify-center">
                        <div className="mb-2 text-sm font-semibold text-gray-700">
                          {t('quote.quote1')}
                        </div>
                        {drawingBoardRef?.current ? (
                          <div 
                            dangerouslySetInnerHTML={{ 
                              __html: drawingBoardRef.current.querySelector('svg')?.outerHTML || '' 
                            }} 
                            className="max-w-full"
                            style={{ 
                              transform: 'scale(0.75)', 
                              transformOrigin: 'top left',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center'
                            }}
                          />
                        ) : (
                          <p className="text-gray-500 text-xs">{t('quote.drawingLoading')}</p>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
                  <tr>
                    <td className="border border-gray-300 px-3 py-2"></td>
                    <td className="border border-gray-300 px-3 py-2 text-right"></td>
                    <td className="border border-gray-300 px-3 py-2 text-right font-semibold">
                      {formatPrice(prices.totalPrice * quantity)}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-right">21%</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-3 py-2 font-semibold" colSpan="2">{t('common.subtotal')}</td>
                    <td className="border border-gray-300 px-3 py-2 text-right font-semibold">
                      {formatPrice(prices.totalPrice * quantity)}
                    </td>
                    <td className="border border-gray-300 px-3 py-2"></td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-3 py-2" colSpan="2">{t('quote.vat21')}</td>
                    <td className="border border-gray-300 px-3 py-2 text-right">
                      {formatPrice(prices.totalPrice * quantity * 0.21)}
                    </td>
                    <td className="border border-gray-300 px-3 py-2"></td>
                  </tr>
                  <tr className="bg-orange-50 font-bold">
                    <td className="border border-gray-300 px-3 py-2" colSpan="2">{t('common.total')}</td>
                    <td className="border border-gray-300 px-3 py-2 text-right text-orange-600">
                      {formatPrice(prices.totalPrice * quantity * 1.21)}
                    </td>
                    <td className="border border-gray-300 px-3 py-2"></td>
                  </tr>
                </tbody>
              </table>
              
              {/* Warranty and Certification Information - shown once at bottom */}
              <div className="mt-4 text-xs text-gray-700 space-y-1">
                <div>- Police certification safe living</div>
                <div>- SKG2** certification</div>
                <div>- 15 years factory warranty on plastic frames</div>
                <div>- 10 years supplier warranty on glass</div>
                <div>- 10 years supplier warranty on window and door hardware manufacturer</div>
                <div>- 5 years warranty on installation</div>
              </div>
            </div>

          {/* Closing Text */}
          <div className="mb-4 text-sm text-gray-800 leading-relaxed">
            <div className="mb-3">
              <h3 className="font-bold mb-2">{t('quote.theSteps')}</h3>
              <p className="mb-2">
                {t('quote.stepsDescription')}
              </p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>{t('quote.step1')}</li>
                <li>{t('quote.step2')}</li>
                <li>{t('quote.step3')}</li>
              </ol>
            </div>
            <p className="mb-3">
              {t('quote.closingMessage')}
            </p>
            <p className="mb-2">
              {t('quote.hopeMessage')}
            </p>
            <p className="mt-4">
              <strong>{t('quote.kindRegards')}</strong><br />
              bestonline Kozijnen
            </p>
          </div>

          {/* Conditions Section */}
          <div className="mt-8 pt-4 border-t-2 border-gray-300">
            <Label className="text-sm font-semibold mb-3 block">{t('quote.conditions') || 'Conditions'}</Label>
            <YesNoToggle
              label={t('quote.subjectToFinance') || 'Subject to finance?'}
              value={clientDetails.subjectToFinance}
              onChange={(value) => setClientDetails({...clientDetails, subjectToFinance: value})}
            />
          </div>

          {/* Signature Section at Bottom */}
          <div className="mt-8 pt-4 border-t-2 border-gray-300">
            <Label className="text-sm font-semibold mb-2 block">{t('quote.clientSignature')}</Label>
            {isEditing ? (
              <div>
                <canvas
                  ref={signatureCanvasRef}
                  width={500}
                  height={100}
                  className="border-2 border-gray-300 rounded cursor-crosshair bg-white"
                  style={{ width: '100%', maxWidth: '500px', height: '100px' }}
                  onMouseDown={(e) => {
                    setIsDrawing(true);
                    const canvas = signatureCanvasRef.current;
                    if (!canvas) return;
                    const rect = canvas.getBoundingClientRect();
                    const scaleX = canvas.width / rect.width;
                    const scaleY = canvas.height / rect.height;
                    const ctx = canvas.getContext('2d');
                    ctx.strokeStyle = '#000000';
                    ctx.lineWidth = 2;
                    ctx.lineCap = 'round';
                    ctx.lineJoin = 'round';
                    ctx.beginPath();
                    ctx.moveTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
                  }}
                  onMouseMove={(e) => {
                    if (!isDrawing) return;
                    const canvas = signatureCanvasRef.current;
                    if (!canvas) return;
                    const rect = canvas.getBoundingClientRect();
                    const scaleX = canvas.width / rect.width;
                    const scaleY = canvas.height / rect.height;
                    const ctx = canvas.getContext('2d');
                    ctx.lineTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
                    ctx.stroke();
                  }}
                  onMouseUp={() => {
                    setIsDrawing(false);
                    const canvas = signatureCanvasRef.current;
                    if (canvas) {
                      const signatureData = canvas.toDataURL('image/png');
                      setClientDetails({...clientDetails, signature: signatureData});
                    }
                  }}
                  onMouseLeave={() => setIsDrawing(false)}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    setIsDrawing(true);
                    const canvas = signatureCanvasRef.current;
                    if (!canvas) return;
                    const rect = canvas.getBoundingClientRect();
                    const scaleX = canvas.width / rect.width;
                    const scaleY = canvas.height / rect.height;
                    const ctx = canvas.getContext('2d');
                    ctx.strokeStyle = '#000000';
                    ctx.lineWidth = 2;
                    ctx.lineCap = 'round';
                    ctx.lineJoin = 'round';
                    const touch = e.touches[0];
                    ctx.beginPath();
                    ctx.moveTo((touch.clientX - rect.left) * scaleX, (touch.clientY - rect.top) * scaleY);
                  }}
                  onTouchMove={(e) => {
                    e.preventDefault();
                    if (!isDrawing) return;
                    const canvas = signatureCanvasRef.current;
                    if (!canvas) return;
                    const rect = canvas.getBoundingClientRect();
                    const scaleX = canvas.width / rect.width;
                    const scaleY = canvas.height / rect.height;
                    const ctx = canvas.getContext('2d');
                    const touch = e.touches[0];
                    ctx.lineTo((touch.clientX - rect.left) * scaleX, (touch.clientY - rect.top) * scaleY);
                    ctx.stroke();
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    setIsDrawing(false);
                    const canvas = signatureCanvasRef.current;
                    if (canvas) {
                      const signatureData = canvas.toDataURL('image/png');
                      setClientDetails({...clientDetails, signature: signatureData});
                    }
                  }}
                />
                <div className="flex gap-2 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const canvas = signatureCanvasRef.current;
                      if (canvas) {
                        const ctx = canvas.getContext('2d');
                        ctx.clearRect(0, 0, canvas.width, canvas.height);
                        setClientDetails({...clientDetails, signature: ''});
                      }
                    }}
                    className="text-xs"
                  >
                    {t('quote.clear')}
                  </Button>
                  <p className="text-xs text-gray-600 self-center">{t('quote.signatureInstructions')}</p>
                </div>
              </div>
            ) : (
              <div>
                {clientDetails.signature && clientDetails.signature.startsWith('data:image') ? (
                  <img src={clientDetails.signature} alt="Signature" className="border-b-2 border-gray-400 pb-1 max-h-[100px] max-w-full object-contain" />
                ) : (
                  <div className="border-b-2 border-gray-400 pb-1 h-[100px] flex items-center justify-center text-gray-400 italic">
                    {t('quote.noSignature')}
                  </div>
                )}
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-1 text-orange-600 hover:text-orange-700 print:hidden text-xs mt-2"
                  title={t('quote.editSignature')}
                >
                  <Pencil className="w-3 h-3" />
                  <span>{t('quote.edit')}</span>
                </button>
              </div>
            )}
          </div>

          {/* Save & Preview Button - At Bottom */}
          {isEditing && (
            <div className="mt-6 mb-4 print:hidden">
              <Button 
                onClick={() => {
                  setIsEditing(false);
                  setIsSaved(true);
                }}
                className="bg-orange-600 hover:bg-orange-700 h-8 text-sm px-4"
              >
                {t('quote.savePreview')}
              </Button>
            </div>
          )}

          {/* Action Buttons */}
          {/* Positioned directly below the table */}
          <div
            className="flex justify-end gap-4 mt-6 mb-4 print:hidden"
            style={{ width: '100%' }}
          >
            <Button variant="outline" onClick={handlePrint}>
              {t('quote.print')}
            </Button>
            <Button 
              onClick={handleExportPDF} 
              className="bg-orange-600 hover:bg-orange-700"
              disabled={isEditing || !isSaved || !clientDetails.signature}
            >
              {t('quote.exportAsPDF')}
            </Button>
          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
};

export default Quote;
